/**
 * Demo: what the address book's record panel shows beyond the person
 * (concierge-desktop screens/address-book.md, The record panel, Sample data).
 *
 * - **Documents** (`credentialsFor`): Priya Nadar's four from the spec (the
 *   CSCS card expired, insurance expiring, the induction checked by Grace
 *   Hill, a signed NDA); Henrik Larsson's "Expired shown" board (a CSCS card
 *   replaced today); a few more so other people differ. Tom Hale holds none.
 *   Files are demo only (`NEEDS_API.uploads`), under `public/demo/uploads`.
 * - **History** for the summary strip (`NEEDS_API.visitHistory`): the next
 *   booked day and who is on site, by person. The count and last visit are
 *   the cast's own (`demo-data.ts`).
 *
 * Every date is relative to today, so the demo reads the same any day.
 */

import type { ApiCredential, ApiCredentialFile } from '../services/api-types';

/** An ISO instant `days` from today (negative: ago), at a time of day. */
function on(days: number, hh = 9, mm = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hh, mm, 0, 0);
  return d.toISOString();
}

const CARD: ApiCredentialFile = {
  name: 'CSCS card.jpg',
  mimeType: 'image/jpeg',
  url: 'demo/uploads/skills-card.jpg',
};
const INSURANCE: ApiCredentialFile = {
  name: 'Insurance certificate.pdf',
  mimeType: 'application/pdf',
  url: 'demo/uploads/insurance-certificate.pdf',
};
const NDA: ApiCredentialFile = {
  name: 'Signed NDA.pdf',
  mimeType: 'application/pdf',
  url: 'demo/uploads/signed-nda.pdf',
};

const GRACE = { name: 'Grace Hill' };

export const DEMO_CREDENTIALS: Readonly<Record<string, readonly ApiCredential[]>> = {
  'p-priya': [
    {
      _id: 'c-priya-cscs',
      label: 'CSCS card',
      kind: 'card',
      method: 'docCapture',
      assurance: 'document',
      reqId: 'req-cscs',
      issuedAt: on(-1840),
      expiresAt: on(-14, 23, 59),
      file: CARD,
    },
    {
      _id: 'c-priya-insurance',
      label: 'Public liability insurance',
      kind: 'insurance',
      method: 'docCapture',
      assurance: 'document',
      reqId: 'req-insurance',
      issuedAt: on(-346),
      expiresAt: on(19, 23, 59),
      file: INSURANCE,
    },
    {
      _id: 'c-priya-induction',
      label: 'Site induction · Hopper House',
      kind: 'induction',
      method: 'staffCheck',
      assurance: 'staff',
      reqId: 'req-induction',
      issuedAt: on(-185),
      expiresAt: on(180, 23, 59),
      verifiedBy: GRACE,
    },
    {
      _id: 'c-priya-nda',
      label: 'Signed NDA',
      kind: 'document',
      method: 'declaration',
      assurance: 'self',
      reqId: 'req-nda',
      issuedAt: on(-185),
      expiresAt: null,
      file: NDA,
    },
  ],
  'p-henrik': [
    {
      _id: 'c-henrik-cscs-new',
      label: 'CSCS card',
      kind: 'card',
      method: 'docCapture',
      assurance: 'document',
      reqId: 'req-cscs',
      issuedAt: on(0, 8, 40),
      expiresAt: on(1800, 23, 59),
      file: CARD,
    },
    {
      _id: 'c-henrik-cscs-old',
      label: 'CSCS card',
      kind: 'card',
      method: 'docCapture',
      assurance: 'document',
      reqId: 'req-cscs',
      issuedAt: on(-1920),
      expiresAt: on(-116, 23, 59),
      file: CARD,
    },
    {
      _id: 'c-henrik-insurance',
      label: 'Public liability insurance',
      kind: 'insurance',
      method: 'docCapture',
      assurance: 'document',
      reqId: 'req-insurance',
      issuedAt: on(-346),
      expiresAt: on(19, 23, 59),
      file: INSURANCE,
    },
    {
      _id: 'c-henrik-induction',
      label: 'Site induction · Hopper House',
      kind: 'induction',
      method: 'staffCheck',
      assurance: 'staff',
      reqId: 'req-induction',
      issuedAt: on(-40),
      expiresAt: on(325, 23, 59),
      verifiedBy: GRACE,
    },
    {
      _id: 'c-henrik-nda',
      label: 'Signed NDA',
      kind: 'document',
      method: 'declaration',
      assurance: 'self',
      reqId: 'req-nda',
      issuedAt: on(-60),
      expiresAt: null,
      file: NDA,
    },
  ],
  'p-daniel': [
    {
      _id: 'c-daniel-induction',
      label: 'Site induction · Hopper House',
      kind: 'induction',
      method: 'staffCheck',
      assurance: 'staff',
      reqId: 'req-induction',
      issuedAt: on(-45),
      expiresAt: on(320, 23, 59),
      verifiedBy: { name: 'Marcus Webb' },
    },
    {
      _id: 'c-daniel-insurance',
      label: 'Public liability insurance',
      kind: 'insurance',
      method: 'docCapture',
      assurance: 'document',
      reqId: 'req-insurance',
      issuedAt: on(-200),
      expiresAt: on(165, 23, 59),
      file: INSURANCE,
    },
    // Visit-scoped: it belongs to one visit, so the record leaves it out.
    {
      _id: 'c-daniel-rams',
      label: 'RAMS · lift 3 refurbishment',
      kind: 'document',
      method: 'docCapture',
      assurance: 'document',
      reqId: 'req-rams',
      visitId: 'v-daniel',
      issuedAt: on(-2),
      expiresAt: null,
      file: INSURANCE,
    },
  ],
  'p-chidi': [
    {
      _id: 'c-chidi-nda',
      label: 'Signed NDA',
      kind: 'document',
      method: 'declaration',
      assurance: 'self',
      reqId: 'req-nda',
      issuedAt: on(-255),
      expiresAt: null,
      file: NDA,
    },
  ],
  'p-grace': [
    {
      _id: 'c-grace-food',
      label: 'Food hygiene certificate',
      kind: 'certificate',
      method: 'staffCheck',
      assurance: 'staff',
      reqId: 'req-food-hygiene',
      issuedAt: on(-390),
      expiresAt: on(-3, 23, 59),
      verifiedBy: GRACE,
    },
  ],
};

/** The summary strip's extras: the next booked day and today's sign-in. */
export interface DemoHistory {
  /** Days ago of the first visit; else the day after they joined the book. */
  readonly first?: number;
  /** Days from today to the next booked day, and its start (HH:MM). */
  readonly next?: readonly [days: number, time: string];
  /** Signed in today at this time and still on site. */
  readonly hereSince?: string;
}

export const DEMO_HISTORY: Readonly<Record<string, DemoHistory>> = {
  'p-priya': { first: 280, next: [7, '09:00'] },
  'p-henrik': { hereSince: '09:10', next: [1, '09:00'] },
  'p-chidi': { next: [1, '10:00'] },
  'p-daniel': { next: [1, '08:00'] },
  'p-elena': { next: [1, '11:00'] },
  'p-sarah': { next: [4, '14:00'] },
};

/** The ISO instant for `HH:MM`, `days` from today. */
export function demoAt(days: number, time: string): string {
  const [hh, mm] = time.split(':').map(Number);
  return on(days, hh, mm);
}
